/**
 * DPDP Consent Manager Utility
 * Implements compliant consent state management adhering to India's
 * Digital Personal Data Protection (DPDP) Act, 2023.
 */

export interface ConsentPurposes {
  necessary: boolean;      // Core site security, navigation & form delivery (always active)
  analytics: boolean;      // Visitor metrics, usage insights (optional, off by default)
  communications: boolean; // Newsletters, event notices & updates (optional, off by default)
}

export type ConsentStatus = 'accepted_all' | 'rejected_all' | 'custom' | 'withdrawn';

export interface ConsentRecord {
  consentId: string;
  timestamp: string;
  version: string;
  domain: string;
  status: ConsentStatus;
  purposes: ConsentPurposes;
}

export const CONSENT_STORAGE_KEY = 'dpdp_consent_preferences';
export const CONSENT_EVENT_NAME = 'dpdp_consent_updated';
export const CONSENT_OPEN_EVENT = 'dpdp_open_preferences';
export const CONSENT_VERSION = '1.0';

/**
 * Generate a pseudo-random identifier for the consent record (without tracking user identity)
 */
const generateConsentId = (): string => {
  return 'dpdp_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
};

/**
 * Retrieves the stored consent record from localStorage if it exists and is valid.
 */
export const getStoredConsent = (): ConsentRecord | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed && parsed.purposes && typeof parsed.purposes.necessary === 'boolean') {
      return parsed;
    }
    return null;
  } catch (e) {
    console.warn('[DPDP] Error reading stored consent:', e);
    return null;
  }
};

/**
 * Saves or updates user consent choices and dispatches notification events.
 */
export const saveConsent = (
  purposes: Omit<ConsentPurposes, 'necessary'> & { necessary?: boolean },
  status: ConsentStatus
): ConsentRecord => {
  const existing = getStoredConsent();
  const record: ConsentRecord = {
    consentId: existing?.consentId || generateConsentId(),
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    domain: window.location.hostname,
    status,
    purposes: {
      necessary: true, // Always locked to true for core operation
      analytics: Boolean(purposes.analytics),
      communications: Boolean(purposes.communications),
    },
  };

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.error('[DPDP] Failed to write consent to storage:', e);
  }

  // Dispatch custom window event for integrations/trackers
  const event = new CustomEvent(CONSENT_EVENT_NAME, { detail: record });
  window.dispatchEvent(event);

  // If analytics was rejected or withdrawn, ensure any future or existing analytics tracking is notified
  if (!record.purposes.analytics) {
    // Prevent or halt analytics tracking
    if ((window as any)['ga-disable']) {
      (window as any)['ga-disable'] = true;
    }
  }

  return record;
};

/**
 * Fully withdraws all optional consents (Withdrawal Symmetry - CFE-06).
 */
export const withdrawAllConsent = (): ConsentRecord => {
  return saveConsent(
    {
      necessary: true,
      analytics: false,
      communications: false,
    },
    'withdrawn'
  );
};

/**
 * Helper to check if a specific purpose has valid, active consent.
 */
export const hasConsentFor = (purpose: keyof ConsentPurposes): boolean => {
  if (purpose === 'necessary') return true;
  const current = getStoredConsent();
  if (!current) return false;
  return Boolean(current.purposes[purpose]);
};

/**
 * Triggers the preferences dialog to open.
 */
export const openConsentPreferences = (): void => {
  if (typeof window !== 'undefined') {
    if (window.location.hash !== '#revealed-modal') {
      window.location.hash = 'revealed-modal';
    }
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
  }
};

// Expose standard API on window for external verification, DPDP scanners & third-party scripts
if (typeof window !== 'undefined') {
  (window as any).DPDPConsent = {
    getConsent: getStoredConsent,
    hasConsentFor,
    saveConsent,
    withdrawAllConsent,
    openPreferences: openConsentPreferences,
    version: CONSENT_VERSION,
  };
}
